import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Checkbox,
  Flex,
  Input,
  Text,
  chakra,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { FieldLabel, FieldRoot } from '~/components/common/Field'

export type EmissionLinkingFieldProps = {
  /** id 衝突回避の prefix (= modal ごとに一意)。 */
  idPrefix: string
  /** 会社情報 (登録小売電気事業者 ID) が登録済みか。 false で checkbox 非活性 (AC-BSP-087)。 */
  registered: boolean
  /** 会社情報 ID (5 文字英数)。 未登録時は空文字。 */
  retailerId: string
  /**
   * 会社情報の取得が失敗中か (= `useCompanyInfo().isError`)。 未登録 (`!registered`)
   * と取得失敗はどちらも checkbox 非活性という結果は同じだが、 ヒント文言は区別する
   * (= 一時的な取得失敗を恒久的な未登録と誤認させない)。
   */
  fetchFailed: boolean
  /**
   * 会社情報を取得中か (= `useCompanyInfo().isLoading`)。 取得中は登録の有無が分からないので、 checkbox は
   * 非活性のまま理由を「読み込み中」にし、 未登録の理由は取得が終わって未登録と分かってから出す
   * (#12136 Des 判断 2026-10-27。 未登録の理由を取得中にも出すと、 登録済みの組織にも事実と違う理由が出る)。
   */
  isLoading: boolean
  linked: boolean
  alpha: string
  alphaError?: string
  onLinkedChange: (linked: boolean) => void
  onAlphaChange: (alpha: string) => void
  /**
   * JEPX自由記述欄見出しの右端に出す文字数カウンタ (例:「12 / 70」)。
   * 直接入力できる自由記述欄を持つ呼び出し元 ([[MemoFields]]) だけが渡す。
   * 渡さない呼び出し元 (= 自由記述欄を持たない [[BulkCreateModal]]) は
   * 従来どおりラベルのみ表示する。
   */
  freeTextCounter?: { length: number; max: number }
  /**
   * JEPX自由記述欄見出しの後ろに出す、 欄を誰と共通で持つかの表記 (例:「(親+子共通)」)。
   * 2 Leg で 1 つの欄を持つブロック入札のリンク・ループだけが渡す (design `spot.html`
   * の `#blk-memo-scope`)。
   */
  sharedScope?: string
}

/**
 * 排出係数紐付け (spec spot-bidding.md §7 AC-BSP-083〜087)。
 * checkbox ON で「アルファベット (1文字)」入力 + 判別コメント完成形
 * `[特定契約{会社情報 ID}{英字}]` のインラインプレビューを表示する。
 * 会社情報 ID はシステム管理の会社情報カード登録値を自動使用し、 ユーザーは
 * 英字 1 文字のみ入力する (= note の typo 防止が本機能の目的)。
 * design `spot.html` の共通コンポーネント `js/emission-linking.js` に対応。
 */
export const EmissionLinkingField = ({
  idPrefix,
  registered,
  retailerId,
  fetchFailed,
  isLoading,
  linked,
  alpha,
  alphaError,
  onLinkedChange,
  onAlphaChange,
  freeTextCounter,
  sharedScope,
}: EmissionLinkingFieldProps): React.ReactElement => {
  const { t } = useTranslation()
  const alphaLabel = t('page:biddingSpot.emission.alphaLabel')
  // 未登録の理由だけ hover に寄せる。 取得失敗は一時的なエラーなので画面に出したまま
  // にする (2026-08-24 デザイナー判断)。 取得中は「読み込み中」の理由を同じ経路で出す
  // (操作できなくした所には必ず理由を出す。 UI 辞書 action-reason.md、 #12136)。
  const isUnregistered = !registered && !fetchFailed && !isLoading
  const disabledHint = isLoading
    ? t('page:biddingSpot.emission.loadingHint')
    : isUnregistered
      ? t('page:biddingSpot.emission.unregisteredHint')
      : undefined
  const disabledHintId = `${idPrefix}-emissionUnregisteredHint`
  return (
    <Box>
      {/* JEPX自由記述欄グループの見出し。 排出係数紐付けは判別コメントを
          `memo_jepx` (= JEPX自由記述欄) に入れる UI なので、 design `spot.html`
          (`#bnew-memo-jepx` の form label → 排出係数 slot の順) と同じくこの
          グループの見出しを持つ (2026-08-24 デザイナー指摘)。
          カウンタは自由記述欄を持つ呼び出し元 ([[MemoFields]]) だけが
          `freeTextCounter` で渡す (issue #6154。 8080 の「0 / 70」相当。
          直入力欄そのものは MemoFields 側の textarea が持つ)。
          form label は共通の [[FieldLabel]] を使う (DS `FieldLabel` は `FieldRoot` の
          context 外だと**何も描画しない**ため `FieldRoot` で包む。 2026-08-24 実測)。
          見出しの下は 4px 空ける (2026-08-24 デザイナー指摘)。 */}
      <FieldRoot marginBottom={1}>
        <Flex alignItems="center" justifyContent="space-between" width="100%">
          <Flex alignItems="baseline" gap={1}>
            <FieldLabel>{t('page:biddingSpot.form.memoJepx')}</FieldLabel>
            {sharedScope !== undefined && (
              <Text color="Text.midEmphasis" fontSize="md">
                {sharedScope}
              </Text>
            )}
          </Flex>
          {freeTextCounter !== undefined && (
            <Text color="Text.lowEmphasis" fontSize="sm">
              {`${freeTextCounter.length} / ${freeTextCounter.max}`}
            </Text>
          )}
        </Flex>
      </FieldRoot>
      {/* 未登録の理由は hover の tooltip で出す。 DS `Tooltip` は child に handler を
          付けて動くため、 非活性の control は器で包む必要がある ([[ButtonTooltip]] が
          その器を持つ)。 [design 差異] 8080 は今もインラインの `.form-hint` で出して
          いる (`js/emission-linking.js`)。 実装が先行する形なので、 design-desk 側の
          プロトも追随させること。 */}
      <ButtonTooltip content={disabledHint}>
        <Checkbox
          checked={linked && registered}
          disabled={!registered}
          inputProps={{
            'aria-describedby':
              disabledHint === undefined ? undefined : disabledHintId,
          }}
          onCheckedChange={(details: { checked: boolean | 'indeterminate' }) =>
            onLinkedChange(details.checked === true)
          }
        >
          {t('page:biddingSpot.emission.toggle')}
        </Checkbox>
      </ButtonTooltip>
      {/* tooltip は**見た目だけ**の経路 (hover しないと出ない)。 押せない理由を
          支援技術から失わないよう、 文言自体は視覚的に隠して DOM に残し、
          checkbox の `aria-describedby` で結ぶ (8080 も input に aria-describedby を
          張っている)。 */}
      {disabledHint !== undefined && (
        <chakra.span
          css={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
            clipPath: 'inset(50%)',
            whiteSpace: 'nowrap',
          }}
          id={disabledHintId}
        >
          {disabledHint}
        </chakra.span>
      )}
      {/* 取得失敗は一時的なエラーなので、 未登録と違って画面に出したまま残す
          (2026-08-24 デザイナー判断)。 表示は grey text ではなく Alert
          (同日デザイナー指摘)。 status / role は同じ画面の読み込み失敗バナー
          (`SpotBidding/index.tsx` の `loadError`) に揃える — DS `Alert` は
          `role` を既定で持たないので明示する (2026-08-24 実測)。 */}
      {!registered && fetchFailed && (
        <Box marginTop="2">
          <Alert
            description={t('page:biddingSpot.emission.fetchErrorHintBody')}
            role="alert"
            status="error"
            title={t('page:biddingSpot.emission.fetchErrorHintTitle')}
          />
        </Box>
      )}
      {registered && linked && (
        <Box
          marginTop="2"
          padding="3"
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="md"
          backgroundColor="Surface.secondary"
        >
          <Flex alignItems="center" flexWrap="wrap" gap="2">
            {/* 判別コメントの固定接頭辞 / 接尾辞は JEPX wire format リテラル (翻訳対象外)。
                `EMISSION_COMMENT_PREFIX` (~/pages/SpotBidding/models/emissionLinking) と
                同一の業務データ定数値であり、 locale を切り替えても JEPX へ実際に送信する
                値と表示を一致させる必要があるため i18n 化しない。 */}
            {/* eslint-disable-next-line ebalance/no-hardcoded-japanese -- JEPX wire format
                リテラル (業務データ定数値、翻訳対象外。上のコメント参照) */}
            <Text fontWeight="semibold">[特定契約</Text>
            <Box textAlign="center">
              <Text fontWeight="semibold" textDecoration="underline">
                {retailerId}
              </Text>
              <Text color="Text.lowEmphasis" fontSize="sm">
                {t('page:biddingSpot.emission.markerId')}
              </Text>
            </Box>
            <Box textAlign="center">
              <Input
                width="48px"
                aria-label={alphaLabel}
                autoCapitalize="characters"
                id={`${idPrefix}-emissionAlpha`}
                maxLength={1}
                onChange={(e) => onAlphaChange(e.target.value.toUpperCase())}
                placeholder="A"
                value={alpha}
              />
              <Text color="Text.lowEmphasis" fontSize="sm">
                {t('page:biddingSpot.emission.markerAlpha')}
              </Text>
            </Box>
            <Text fontWeight="semibold">]</Text>
            <Box>
              <Text color="Text.lowEmphasis" fontSize="sm">
                {t('page:biddingSpot.emission.legendId')}
              </Text>
              <Text color="Text.lowEmphasis" fontSize="sm">
                {t('page:biddingSpot.emission.legendAlpha')}
              </Text>
            </Box>
          </Flex>
          {/* FieldRoot(invalid) の context 外なので FieldErrorText でなく素の Text で
              エラー色表示する (= chakra v3 Field の ErrorText は invalid context 必須)。 */}
          {alphaError !== undefined && (
            <Text marginTop="1" color="Text.negativeHigh" fontSize="sm">
              {alphaError}
            </Text>
          )}
        </Box>
      )}
    </Box>
  )
}
